import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
} from "@mui/material";

import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  Timestamp,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  orderBy,
} from "firebase/firestore";

export default function SeatingCategory() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  const [propertyId, setPropertyId] = useState("");
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const [catname, setCatname] = useState("");
  const [status, setStatus] = useState("Active");

  const [editId, setEditId] = useState("");
  const [editCat, setEditCat] = useState("");
  const [editStatus, setEditStatus] = useState("");

  const [priority, setPriority] = useState(1);

  const [editPriority, setEditPriority] = useState(1);

  /* ================= FETCH PROPERTY ID ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const roleSnap = await getDoc(doc(db, "authroles", user.uid));
      if (!roleSnap.exists()) {
        Toast.fire({ icon: "error", title: "Property not assigned" });
        return;
      }

      setPropertyId(roleSnap.data().propertyId);
    });

    return () => unsub();
  }, []);

  /* ================= FETCH SEATING CATEGORIES ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "seatingcats"),
      where("propertyId", "==", propertyId),
      orderBy('priority', 'asc')
    );

    const unsub = onSnapshot(q, (snap) => {
      setCategories(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= ADD ================= */
  const handleAdd = async () => {
    if (!catname) {
      Toast.fire({ icon: "error", title: "Enter category name" });
      return;
    }

    await addDoc(collection(db, "seatingcats"), {
      propertyId,
      catname,
      status,
      createdAt: Timestamp.now(),
      priority: Number(priority),

    });

    setCatname("");
    setStatus("Active");
    setOpenAdd(false);

    Toast.fire({ icon: "success", title: "Category added" });
  };

  /* ================= EDIT ================= */
  const openEditModal = (row) => {
    setEditId(row.id);
    setEditCat(row.catname);
    setEditStatus(row.status);
    setEditPriority(row.priority || 1);
    setOpenEdit(true);
  };

  const handleUpdate = async () => {
    await setDoc(
      doc(db, "seatingcats", editId),
      {
        catname: editCat,
        status: editStatus,
        updatedAt: Timestamp.now(),
        priority: Number(editPriority),

      },
      { merge: true }
    );

    setOpenEdit(false);
    Toast.fire({ icon: "success", title: "Category updated" });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "seatingcats", id));
    Toast.fire({ icon: "success", title: "Category deleted" });
  };

  /* ================= FILTER ================= */
  const filtered = categories.filter((c) =>
    c.catname.toLowerCase().includes(search.toLowerCase())
  );

  /* ================= UI ================= */
  return (
    <Box className=" p-4">
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Seating Categories</Typography>
            <Button variant="contained" onClick={() => setOpenAdd(true)}>
              Add Category
            </Button>
          </Stack>

          <TextField
            size="small"
            placeholder="Search category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row, i) => (
                  <TableRow key={row.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{row.catname}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={row.status === "Active" ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{row.priority}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openEditModal(row)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(row.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ADD MODAL */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth>
        <DialogTitle>Add Seating Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category Name"
            value={catname}
            onChange={(e) => setCatname(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ mt: 2 }}
          >
            <option value="Active">Active</option>
            <option value="DeActive">DeActive</option>
          </TextField>
          <TextField
            fullWidth
            type="number"
            label="Priority (Lower = Top)"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth>
        <DialogTitle>Edit Seating Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category Name"
            value={editCat}
            onChange={(e) => setEditCat(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            select
            label="Status"
            value={editStatus}
            onChange={(e) => setEditStatus(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ mt: 2 }}
          >
            <option value="Active">Active</option>
            <option value="DeActive">DeActive</option>
          </TextField>
          <TextField
            fullWidth
            type="number"
            label="Priority"
            value={editPriority}
            onChange={(e) => setEditPriority(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
